/**
 * Financial Statement Service
 * 
 * Handles all financial statement-related API calls
 */

import { apiRequest } from './apiClient';
import { AnnualReport } from '../store/slices/annualReportSlice';
import { EmptyResponse, SyncfusionSearchParam, UpdateAnnualReportRequest } from './types';
import { CycleItem, ProcessItem } from './hierarchyService';
import { parseCommaSeparated } from '../utils/stringUtils';

/**
 * Annual Reports API
 */
export interface GetAnnualReportsResponse {
  data: AnnualReport[];
  code?: string;
  message?: string;
}

export const getAnnualReports = async (companyId: string ): Promise<AnnualReport[]> => {
  try {
    const response = await apiRequest<GetAnnualReportsResponse>(
      'GET',
      `/annual-reports/by-company/${companyId}`,
      undefined,
      true // Authentication required
    );
    return response.data;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    // Wrap unexpected errors
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch annual reports',
    };
  }
};

/**
 * Update Annual Report (e.g. Fiscal Year End Date)
 */
export const updateAnnualReport = async (data: UpdateAnnualReportRequest): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateAnnualReportRequest>(
      'PUT',
      '/annual-reports',
      data,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update annual report',
    };
  }
};

/**
 * Rollover Annual Report to Next Year
 */
export const rolloverAnnualReport = async (annualReportId: string): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, string>(
      'POST',
      '/annual-reports/rollover/' + annualReportId,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to rollover annual report',
    };
  }
};

/**
 * Rollover Annual Report to Next Year
 */
export const deleteAnnualReport = async (annualReportId: string): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, string>(
      'DELETE',
      '/annual-reports/' + annualReportId,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete annual report',
    };
  }
};

/**
 * Financial Statement Line Items API
 */
export interface LineItem {
  Index: number;
  Name: string;
  Amount: number;
  IsImmaterial: boolean;
  CycleIds: string;
  Cycles: string;
  ProcessIds: string;
  Processes: string;
  LineItemType: 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense';
  CreatedAt: string;
  Id: string;
}

export interface GetLineItemsRequest {
  requiresCounts: boolean;
  skip: number;
  take: number;
  search?: SyncfusionSearchParam[];
}

export interface GetLineItemsResponse {
  result: LineItem[];
  count: number;
}

export const getAssets = async (
  financialStatementId: string,
  request: GetLineItemsRequest
): Promise<GetLineItemsResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetLineItemsResponse, typeof requestBody>(
      'POST',
      `/financial-statements/assets/index-grid/${financialStatementId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch assets',
    };
  }
};

export const getLiabilities = async (
  financialStatementId: string,
  request: GetLineItemsRequest
): Promise<GetLineItemsResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetLineItemsResponse, typeof requestBody>(
      'POST',
      `/financial-statements/liabilities/index-grid/${financialStatementId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch liabilities',
    };
  }
};

export const getEquities = async (
  financialStatementId: string,
  request: GetLineItemsRequest
): Promise<GetLineItemsResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetLineItemsResponse, typeof requestBody>(
      'POST',
      `/financial-statements/equities/index-grid/${financialStatementId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch equities',
    };
  }
};

export const getRevenues = async (
  financialStatementId: string,
  request: GetLineItemsRequest
): Promise<GetLineItemsResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetLineItemsResponse, typeof requestBody>(
      'POST',
      `/financial-statements/revenue/index-grid/${financialStatementId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch revenues',
    };
  }
};

export const getExpenses = async (
  financialStatementId: string,
  request: GetLineItemsRequest
): Promise<GetLineItemsResponse> => {
  try {
    const requestBody = {
      requiresCounts: request.requiresCounts,
      skip: request.skip,
      take: request.take,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetLineItemsResponse, typeof requestBody>(
      'POST',
      `/financial-statements/expenses/index-grid/${financialStatementId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch expenses',
    };
  }
};

/**
 * Create Line Item API
 */
export interface CreateLineItemRequest {
  financialStatementId: string;
  index: number;
  name: string;
  amount: number;
  isImmaterial: boolean;
  cycleIds: string[];
  processIds: string[];
  lineItemType: 'asset' | 'liability' | 'equity' | 'revenue' | 'expenses';
}

export const createLineItem = async (
  request: CreateLineItemRequest
): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse, CreateLineItemRequest>(
      'POST',
      '/financial-statements',
      request,
      true // Authentication required
    );
    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create line item',
    };
  }
};

/**
 * Update Line Item API
 */
export interface UpdateLineItemRequest {
  statementLineItemId: string;
  financialStatementId: string;
  name: string;
  amount: number;
  isImmaterial: boolean;
  cycleIds: string[];
  processIds: string[];
  lineItemType: 'asset' | 'liability' | 'equity' | 'revenue' | 'expenses';
}

export const updateLineItem = async (
  request: UpdateLineItemRequest
): Promise<LineItem> => {
  try {
    const response = await apiRequest<EmptyResponse, UpdateLineItemRequest>(
      'PUT',
      `/financial-statements`,
      request,
      true // Authentication required
    );
    return response.data;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update line item',
    };
  }
};

/**
 * Delete Line Item API
 */
export const deleteLineItem = async (id: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/financial-statements/${id}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete line item',
    };
  }
};

/**
 * Helper functions to parse comma-separated backend data
 */
export const parseBackendLineItem = (item: LineItem): any => {
  // Parse comma-separated IDs and names
  const cycleIds = parseCommaSeparated(item.CycleIds || '');
  const cycleNames = parseCommaSeparated(item.Cycles || '');
  const processIds = parseCommaSeparated(item.ProcessIds || '');
  const processNames = parseCommaSeparated(item.Processes || '');

  // Create CycleItem[] and ProcessItem[] arrays
  const cycles: CycleItem[] = cycleIds.map((id, index) => {
    const fullName = cycleNames[index] || '';
    // Try to parse "A00 Cycle Test 1" into code and name
    const parts = fullName.match(/^([A-Z0-9]+)\s+(.+)$/);
    return {
      cycleId: id,
      cycleCode: parts ? parts[1] : fullName.split(' ')[0] || fullName,
      cycleName: parts ? parts[2] : fullName,
    };
  });

  const processes: ProcessItem[] = processIds.map((id, index) => {
    const fullName = processNames[index] || '';
    // Try to parse "A03 Process 3" into code and name
    const parts = fullName.match(/^([A-Z0-9.]+)\s+(.+)$/);
    return {
      processId: id,
      processCode: parts ? parts[1] : fullName.split(' ')[0] || fullName,
      processName: parts ? parts[2] : fullName,
      riskAssessmentId: '',
      processOwnerId: '',
      processOwnerName: '',
    };
  });

  // Format amount: positive as $1000, negative as $(1000)
  const formatAmount = (amount: number): string => {
    if (amount < 0) {
      return `$(${Math.abs(amount).toLocaleString()})`;
    }
    return `$${amount.toLocaleString()}`;
  };

  return {
    id: item.Id,
    name: item.Name,
    amount: formatAmount(item.Amount),
    isImmaterial: item.IsImmaterial,
    cycles,
    processes,
    index: item.Index,
    createdAt: item.CreatedAt,
  };
};

/**
 * Save Materiality Threshold API
 */
export interface SaveMaterialityThresholdRequest {
  value: number;
}

export interface SaveMaterialityThresholdResponse {
  success: boolean;
  message?: string;
}

export const saveMaterialityThreshold = async (
  financialStatementId: string,
  value: number
): Promise<SaveMaterialityThresholdResponse> => {
  try {
    const response = await apiRequest<SaveMaterialityThresholdResponse, SaveMaterialityThresholdRequest>(
      'PUT',
      `/financial-statements/materiality/${financialStatementId}/${value}`,
      undefined,
      true // Authentication required
    );
    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to save materiality threshold',
    };
  }
};

/**
 * Save Materiality Description API
 */
export interface SaveMaterialityDescriptionRequest {
  statementId: string;
  description: string | null;
}

export const saveMaterialityDescription = async (
  financialStatementId: string,
  description: string | null
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, SaveMaterialityDescriptionRequest>(
      'PUT',
      `/financial-statements/materiality-description`,
      { statementId: financialStatementId, description },
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to save materiality description',
    };
  }
};
